import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Group,
  Loader,
  Modal,
  MultiSelect,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle,
  IconNews,
  IconPencil,
  IconPlus,
  IconTagOff,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useState, type FormEvent } from 'react';
import { PageHeader } from '@/components/page-header';
import { ApiError, ApiErrorCode, NetworkError } from '@/lib/api-error';
import {
  createHeadline,
  deleteHeadline,
  age,
  listHeadlines,
  updateHeadline,
  type Headline,
  type HeadlineDraft,
} from '@/lib/headlines';
import { listInterests, type Interest } from '@/lib/interests';
import classes from './settings-page.module.css';

/** What the form is for: adding a headline, or editing the one it holds. */
type Subject = { mode: 'create' } | { mode: 'edit'; headline: Headline };

function describeError(error: unknown): string {
  if (error instanceof NetworkError) {
    return 'The API did not answer. It may not be running.';
  }
  if (error instanceof ApiError) return error.message;
  return 'Something went wrong.';
}

/**
 * Client-side checks, mirroring what the server enforces.
 *
 * A convenience so a typo does not cost a round trip — not a control. This
 * bundle is public and modifiable, so the API validates the same fields again
 * and is the only thing that decides what gets stored.
 */
function validate(
  draft: HeadlineDraft,
): Partial<Record<keyof HeadlineDraft, string>> {
  const errors: Partial<Record<keyof HeadlineDraft, string>> = {};

  if (!draft.title.trim()) errors.title = 'Required.';
  if (!draft.source.trim()) errors.source = 'Required.';
  if (!draft.publishedAt) {
    errors.publishedAt = 'Required.';
  } else if (Number.isNaN(new Date(draft.publishedAt).getTime())) {
    errors.publishedAt = 'A date and time.';
  }

  return errors;
}

interface HeadlineFormProps {
  subject: Subject;
  /** The whole vocabulary, loaded once by the page rather than per open. */
  interests: Interest[];
  onClose: () => void;
  onSaved: (headline: Headline, mode: Subject['mode']) => void;
}

/**
 * One form for both adding and editing.
 *
 * The two differ by their title, their submit label and whether an id is being
 * patched. Splitting them in two would duplicate three inputs and their
 * validation to express that.
 *
 * Mounted per subject with a `key`, so the fields start from the right values
 * and an abandoned edit cannot bleed into the next one — the alternative is an
 * effect that resets four pieces of state on every open.
 */
function HeadlineForm({
  subject,
  interests,
  onClose,
  onSaved,
}: HeadlineFormProps) {
  const [draft, setDraft] = useState<HeadlineDraft>(() =>
    subject.mode === 'edit'
      ? {
          title: subject.headline.title,
          source: subject.headline.source,
          // datetime-local wants "YYYY-MM-DDTHH:mm" and no zone.
          publishedAt: subject.headline.publishedAt.slice(0, 16),
          interestIds: subject.headline.interests.map((tag) => tag.id),
        }
      : { title: '', source: '', publishedAt: '', interestIds: [] },
  );
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof HeadlineDraft, string>>
  >({});
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [isSaving, setIsSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();

    const errors = validate(draft);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    const trimmed: HeadlineDraft = {
      title: draft.title.trim(),
      source: draft.source.trim(),
      publishedAt: new Date(draft.publishedAt).toISOString(),
      interestIds: draft.interestIds,
    };

    setIsSaving(true);
    setSubmitError(null);
    try {
      const saved =
        subject.mode === 'edit'
          ? await updateHeadline(subject.headline.id, trimmed)
          : await createHeadline(trimmed);
      onSaved(saved, subject.mode);
      onClose();
    } catch (caught) {
      setSubmitError(caught);
    } finally {
      setIsSaving(false);
    }
  }

  // A conflict is about one field and belongs beside it, not in a banner at
  // the top of a three-field form.
  const conflict =
    submitError instanceof ApiError && submitError.is(ApiErrorCode.CONFLICT)
      ? submitError.message
      : null;

  return (
    <form onSubmit={(event) => void submit(event)} noValidate>
      <Stack gap="md">
        {submitError !== null && conflict === null && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            <Text size="sm">{describeError(submitError)}</Text>
          </Alert>
        )}

        <TextInput
          label="Headline"
          description="As published. Not re-worded — the point is that it reads like something somebody actually wrote."
          placeholder="Madrid's metro will run through the night all summer"
          data-autofocus
          value={draft.title}
          error={fieldErrors.title}
          onChange={(event) =>
            setDraft({ ...draft, title: event.currentTarget.value })
          }
        />

        <TextInput
          label="Source"
          description="Who published it. Shown under the headline so the card reads as a feed."
          placeholder="El País"
          value={draft.source}
          error={fieldErrors.source}
          onChange={(event) =>
            setDraft({ ...draft, source: event.currentTarget.value })
          }
        />

        <TextInput
          type="datetime-local"
          label="Published"
          description="When it was published, not when you added it. The app shows this as an age, so an old story reads as old."
          value={draft.publishedAt}
          error={fieldErrors.publishedAt}
          onChange={(event) =>
            setDraft({ ...draft, publishedAt: event.currentTarget.value })
          }
        />

        <MultiSelect
          label="Interests"
          description="What clicking this says about somebody. The headline is gone in a week; this is what is kept."
          placeholder={
            draft.interestIds.length === 0 ? 'Pick at least one' : undefined
          }
          searchable
          clearable
          nothingFoundMessage="No interest by that name — add it in Settings → Interests."
          data={interests.map((interest) => ({
            value: interest.id,
            label: interest.label,
          }))}
          value={draft.interestIds}
          onChange={(interestIds) => setDraft({ ...draft, interestIds })}
        />

        {/* A warning rather than a validation error. An untagged headline is
            legal — it can be offered and picked like any other — it just
            teaches the platform nothing about whoever picks it. Refusing to
            save one would be the wrong call, and letting it through silently
            would be worse, because nothing downstream ever shows the gap. */}
        {draft.interestIds.length === 0 && (
          <Alert color="yellow" icon={<IconTagOff size={18} />}>
            <Text size="sm">
              With no interests, this headline can still be shown and picked
              &mdash; it just tells us nothing about whoever picks it.
            </Text>
          </Alert>
        )}

        <Group justify="flex-end" gap="sm">
          <Button
            variant="subtle"
            color="gray"
            onClick={onClose}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSaving}>
            {subject.mode === 'edit' ? 'Save changes' : 'Add headline'}
          </Button>
        </Group>

      </Stack>
    </form>
  );
}

interface RemoveFormProps {
  headline: Headline;
  onClose: () => void;
  onRemoved: (headline: Headline) => void;
}

/**
 * Confirmation before a delete.
 *
 * The delete is a hard one — there is no soft-delete column to set — which
 * makes a mis-click unrecoverable except by typing the row back in, tags and
 * all. The tags go with it, by a cascade on the join table: an orphaned link
 * is not history worth keeping.
 */
function RemoveForm({ headline, onClose, onRemoved }: RemoveFormProps) {
  const [error, setError] = useState<unknown>(null);
  const [isRemoving, setIsRemoving] = useState(false);

  async function confirm() {
    setIsRemoving(true);
    try {
      await deleteHeadline(headline.id);
      onRemoved(headline);
      onClose();
    } catch (caught) {
      setError(caught);
    } finally {
      setIsRemoving(false);
    }
  }

  return (
    <Stack gap="md">
      {error !== null && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          <Text size="sm">{describeError(error)}</Text>
        </Alert>
      )}

      <Text size="sm">
        {headline.title} leaves the catalogue, and its interests go with it.
        This deletes the row outright — getting it back means adding it and
        tagging it again. What has already been recorded about people who
        picked it is kept.
      </Text>

      <Group justify="flex-end" gap="sm">
        <Button
          variant="subtle"
          color="gray"
          onClick={onClose}
          disabled={isRemoving}
        >
          Cancel
        </Button>
        <Button color="red" loading={isRemoving} onClick={() => void confirm()}>
          Remove
        </Button>
      </Group>
    </Stack>
  );
}

export function HeadlinesPage() {
  const [headlines, setHeadlines] = useState<Headline[]>([]);
  // The vocabulary the tag picker offers. Loaded alongside the headlines and
  // held here, so opening the form is not a round trip.
  const [interests, setInterests] = useState<Interest[]>([]);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [isLoading, setIsLoading] = useState(true);
  // What each dialog is about, and whether it is open, held separately.
  // Clearing the subject on close would empty the dialog while it is still
  // fading out; leaving it set until the next open keeps a close looking like
  // a close.
  const [subject, setSubject] = useState<Subject | null>(null);
  const [isFormOpen, setFormOpen] = useState(false);
  const [removing, setRemoving] = useState<Headline | null>(null);
  const [isRemoveOpen, setRemoveOpen] = useState(false);

  function openForm(next: Subject) {
    setSubject(next);
    setFormOpen(true);
  }

  function openRemove(headline: Headline) {
    setRemoving(headline);
    setRemoveOpen(true);
  }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        // Both together: a headline with no interests to pick from is a form
        // that cannot be filled in properly, so neither is useful alone.
        const [list, vocabulary] = await Promise.all([
          listHeadlines(),
          listInterests(),
        ]);
        if (cancelled) return;
        setHeadlines(list);
        setInterests(vocabulary);
        setLoadError(null);
      } catch (caught) {
        if (!cancelled) setLoadError(caught);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * The order the API sorts by, held locally so an edit lands in place.
   *
   * It has to match `CATALOGUE_ORDER` in the API exactly: display order first
   * with unranked at the back, then label. Get this wrong and a saved headline
   * appears in one place until the page is reloaded and it moves.
   */
  /**
   * Newest first, matching the API.
   *
   * No hand-ranking here, unlike the languages and reasons: this is a feed, and
   * the only order a feed has is when things were published.
   */
  function sorted(list: Headline[]): Headline[] {
    return [...list].sort(
      (a, b) =>
        new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime(),
    );
  }

  function onSaved(saved: Headline, mode: Subject['mode']) {
    setHeadlines((current) =>
      sorted(
        mode === 'edit'
          ? current.map((item) => (item.id === saved.id ? saved : item))
          : [...current, saved],
      ),
    );
    notifications.show({
      color: 'brand',
      message:
        mode === 'edit' ? 'Headline updated.' : 'Headline added.',
    });
  }

  function onRemoved(removed: Headline) {
    setHeadlines((current) => current.filter((item) => item.id !== removed.id));
    notifications.show({ color: 'gray', message: 'Headline removed.' });
  }

  const header = (
    <PageHeader
      title="Headlines"
      description="Shown in the first run, which asks which of these somebody would actually click."
      actions={
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => openForm({ mode: 'create' })}
          disabled={isLoading || loadError !== null}
        >
          Add headline
        </Button>
      }
    />
  );

  if (isLoading) {
    return (
      <Stack gap="lg" className={classes.page}>
        {header}
        <Center py="xl">
          <Loader size="sm" />
        </Center>
      </Stack>
    );
  }

  if (loadError !== null) {
    return (
      <Stack gap="lg" className={classes.page}>
        {header}
        <Alert
          color="red"
          title="Cannot load the headlines"
          icon={<IconAlertTriangle size={18} />}
        >
          <Text size="sm">{describeError(loadError)}</Text>
        </Alert>
      </Stack>
    );
  }

  return (
    <Stack gap="lg" className={classes.page}>
      {header}

      {headlines.length === 0 ? (
        <Card>
          <Stack gap="xs" align="center" py="xl">
            <IconNews size={28} stroke={1.4} />
            <Text fw={600}>No headlines yet</Text>
            <Text size="sm" c="dimmed" ta="center" maw={420}>
              The catalogue is empty. Add the first headline and it becomes
              available to everything that reads this list.
            </Text>
          </Stack>
        </Card>
      ) : (
        <Card p={0}>
          <Table.ScrollContainer minWidth={520}>
            <Table verticalSpacing="sm" horizontalSpacing="lg">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Headline</Table.Th>
                  <Table.Th w={200}>Interests</Table.Th>
                  <Table.Th w={140}>Source</Table.Th>
                  <Table.Th w={110}>Published</Table.Th>
                  <Table.Th w={90} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {headlines.map((headline) => (
                  <Table.Tr key={headline.id}>
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {headline.title}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {/* The tags are the whole point of this screen and they
                          are invisible everywhere else — nobody using the app
                          ever sees one, so a wrong tag can only be caught
                          here. An untagged row says so in words rather than
                          leaving an empty cell that reads as still loading. */}
                      {headline.interests.length === 0 ? (
                        <Group gap={6} wrap="nowrap" c="yellow">
                          <IconTagOff size={14} stroke={1.7} />
                          <Text size="xs">Untagged</Text>
                        </Group>
                      ) : (
                        <Group gap={4}>
                          {headline.interests.map((tag) => (
                            <Badge key={tag.id} variant="light" color="gray">
                              {tag.label}
                            </Badge>
                          ))}
                        </Group>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {headline.source}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {/* An age rather than a date. These are curated by hand
                          until a refresh job exists, and the first run implies
                          they are recent — a table of three-month-old stories
                          should be obvious at a glance. */}
                      <Text size="sm" c="dimmed">
                        {age(headline.publishedAt)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={`Edit ${headline.title}`}
                          onClick={() => openForm({ mode: 'edit', headline })}
                        >
                          <IconPencil size={16} stroke={1.7} />
                        </ActionIcon>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label={`Remove ${headline.title}`}
                          onClick={() => openRemove(headline)}
                        >
                          <IconTrash size={16} stroke={1.7} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      )}

      <Text size="xs" c="dimmed">
        The first run offers the five newest of these and asks which somebody
        would actually click &mdash; the answer is what the articles feed and
        the characters&rsquo; topics are built from. They are curated by hand
        for now, and the app shows each one&rsquo;s age, so a table of
        three-month-old stories reads as exactly that. The seeded rows are
        invented placeholders; replace them with real ones.
      </Text>

      <Modal
        opened={isFormOpen}
        onClose={() => setFormOpen(false)}
        centered
        title={
          subject?.mode === 'edit'
            ? 'Edit headline'
            : 'Add a headline'
        }
      >
        {subject && (
          <HeadlineForm
            key={subject.mode === 'edit' ? subject.headline.id : 'create'}
            subject={subject}
            interests={interests}
            onClose={() => setFormOpen(false)}
            onSaved={onSaved}
          />
        )}
      </Modal>

      <Modal
        opened={isRemoveOpen}
        onClose={() => setRemoveOpen(false)}
        centered
        title="Remove this headline?"
      >
        {removing && (
          <RemoveForm
            key={removing.id}
            headline={removing}
            onClose={() => setRemoveOpen(false)}
            onRemoved={onRemoved}
          />
        )}
      </Modal>
    </Stack>
  );
}
